import type { ApiResponseEnvelope } from '@/shared/api/types/api-response'
import type {
  PresignedUploadRequest,
  PresignedUploadResponse,
} from '@/shared/api/types/upload-types'
import i18next from 'i18next'
import { GW } from '@/shared/api/endpoints'
import { request } from '@/shared/libs/request'

const PRESIGNED_UPLOAD_PATH = 'admin/uploads/presigned-upload'
const PAYLOAD_TOO_LARGE_STATUS = 413

class UploadPayloadTooLargeError extends Error {
  constructor() {
    super(i18next.t('common.upload.payloadTooLarge'))
    this.name = 'UploadPayloadTooLargeError'
  }
}

function unwrapPresignedResponse(
  payload: ApiResponseEnvelope<PresignedUploadResponse> | PresignedUploadResponse,
): PresignedUploadResponse {
  if ('data' in payload) {
    if (!payload.data) {
      throw new Error(payload.message ?? 'API returned empty data')
    }
    return payload.data
  }

  return payload
}

export async function getPresignedUploadUrl(
  params: PresignedUploadRequest,
): Promise<PresignedUploadResponse> {
  const response = await request.post<
    ApiResponseEnvelope<PresignedUploadResponse> | PresignedUploadResponse
  >(GW.packaging(PRESIGNED_UPLOAD_PATH), params)

  return unwrapPresignedResponse(response.data)
}

export async function putFileToPresignedUrl(params: {
  uploadUrl: string
  httpMethod?: string
  headers?: Record<string, string>
  file: File
}): Promise<void> {
  const method = (params.httpMethod ?? 'PUT').toUpperCase()
  const requestInit: RequestInit = {
    method,
    body: params.file,
  }
  if (params.headers) {
    requestInit.headers = params.headers
  }

  let uploadResponse: Response
  try {
    uploadResponse = await fetch(params.uploadUrl, requestInit)
  } catch {
    throw new Error(i18next.t('common.upload.networkError'))
  }

  if (!uploadResponse.ok) {
    if (uploadResponse.status === PAYLOAD_TOO_LARGE_STATUS) {
      throw new UploadPayloadTooLargeError()
    }
    throw new Error(i18next.t('common.upload.serverError', { status: uploadResponse.status }))
  }
}

export async function uploadFileWithPresign(
  file: File,
): Promise<Pick<PresignedUploadResponse, 'blobName' | 'url'>> {
  const presigned = await getPresignedUploadUrl({
    filename: file.name,
    contentType: file.type || 'application/octet-stream',
    fileSizeBytes: file.size,
    folder: 'uploads',
  })
  await putFileToPresignedUrl({
    uploadUrl: presigned.uploadUrl,
    httpMethod: presigned.httpMethod,
    headers: presigned.headers,
    file,
  })

  return {
    blobName: presigned.blobName,
    url: presigned.url,
  }
}
